import { useCallback, useRef, useState } from "react"
import { v4 as uuid } from "uuid"
import {
  useCreateReplyModeBatchApi,
  useExecuteReplyModeBatchApi,
  useGetReplyModeBatchApi,
  type ReplyModeBatch,
  type ReplyModeBatchFilters,
  type ReplyModeTarget,
} from "@/hooks/api"
import { normalizeApiError } from "@/lib/apiErrors"

type ReplyModeBatchSource = "ids" | "filters"

type ReplyModeBatchPhase =
  | "idle"
  | "creating"
  // Create outcome unknown and the saved snapshot could not be read back yet.
  | "createUnknown"
  | "ready"
  | "executing"
  | "reconciling"
  // Execute outcome unknown and the saved results could not be read back yet.
  | "unknown"

interface CreateReplyModeBatchInput {
  agentId: string
  source: ReplyModeBatchSource
  clientUserIds?: string[]
  filters?: ReplyModeBatchFilters
}

interface ReplyModeBatchState {
  batch: ReplyModeBatch | null
  phase: ReplyModeBatchPhase
  source: ReplyModeBatchSource | null
  interrupted: boolean
  // Retain confirmed intent even if the first execute outcome is unknown.
  requestedMode: ReplyModeTarget | null
}

const INITIAL_STATE: ReplyModeBatchState = {
  batch: null,
  phase: "idle",
  source: null,
  interrupted: false,
  requestedMode: null,
}

interface PendingCreate {
  id: string
  input: CreateReplyModeBatchInput
}

const getStatus = (error: unknown) => normalizeApiError(error).status

/**
 * A 4xx (other than timeout / rate limit) means the server definitely
 * rejected the request. Anything else (network failure, timeout, 5xx,
 * empty body) leaves the outcome unknown and must be reconciled by GET.
 */
const isDefinitiveRejection = (error: unknown) => {
  const status = getStatus(error)
  return (
    status !== null &&
    status >= 400 &&
    status < 500 &&
    status !== 408 &&
    status !== 429
  )
}

const buildCreateBody = (id: string, input: CreateReplyModeBatchInput) =>
  input.source === "ids"
    ? { id, agent_id: input.agentId, client_user_ids: input.clientUserIds }
    : { id, agent_id: input.agentId, filters: input.filters ?? {} }

export interface UseReplyModeBatchOptions {
  onRequestError?: (error: unknown) => void
}

export const useReplyModeBatch = (
  organizationId: string,
  workspaceId: string,
  options: UseReplyModeBatchOptions = {}
) => {
  const { fetchFn: createBatchApi } = useCreateReplyModeBatchApi(
    organizationId,
    workspaceId
  )
  const { fetchFn: getBatchApi } = useGetReplyModeBatchApi(
    organizationId,
    workspaceId
  )
  const { fetchFn: executeBatchApi } = useExecuteReplyModeBatchApi(
    organizationId,
    workspaceId
  )

  const [state, setState] = useState(INITIAL_STATE)
  const stateRef = useRef(state)
  // Synchronous guard against duplicate requests (double clicks, re-entrant
  // handlers) in addition to disabled UI.
  const inFlightRef = useRef(false)
  const generationRef = useRef(0)
  const pendingCreateRef = useRef<PendingCreate | null>(null)
  const onRequestErrorRef = useRef(options.onRequestError)
  onRequestErrorRef.current = options.onRequestError

  // Async handlers and duplicate clicks must see changes before React rerenders.
  const update = useCallback((changes: Partial<ReplyModeBatchState>) => {
    stateRef.current = { ...stateRef.current, ...changes }
    setState(stateRef.current)
  }, [])

  const reset = useCallback(() => {
    generationRef.current += 1
    inFlightRef.current = false
    pendingCreateRef.current = null
    update(INITIAL_STATE)
  }, [update])

  /** Drop a snapshot that has not been executed yet (selection changed). */
  const resetIfNotStarted = useCallback(() => {
    const { batch, phase, requestedMode } = stateRef.current
    if (
      phase === "executing" ||
      phase === "reconciling" ||
      phase === "unknown"
    ) {
      return
    }
    if (batch?.interaction_mode || requestedMode) return
    if (phase === "idle" && !batch) return
    reset()
  }, [reset])

  /** Saved progress plus one page of failures. */
  const readSavedBatch = useCallback(
    async (batchId: string, after?: string) => {
      const result = await getBatchApi({
        urlBuilderParams: { batchId },
        ...(after ? { searchParams: new URLSearchParams({ after }) } : {}),
      })
      return result?.data ?? null
    },
    [getBatchApi]
  )

  /** Replace local progress with saved results; `fallback` if unreadable. */
  const reconcile = useCallback(
    async (
      batchId: string,
      isStale: () => boolean,
      fallback: ReplyModeBatchPhase
    ) => {
      update({ phase: "reconciling" })
      try {
        const saved = await readSavedBatch(batchId)
        if (isStale()) return null
        if (!saved) throw new Error("Missing saved reply mode batch")
        update({ batch: saved, interrupted: true, phase: "ready" })
        return saved
      } catch {
        if (!isStale()) update({ phase: fallback })
        return null
      }
    },
    [readSavedBatch, update]
  )

  const getSelectedPageIds = useCallback(
    async (batchId: string, clientUserIds: string[]) => {
      const searchParams = new URLSearchParams()
      clientUserIds.forEach((id) => searchParams.append("client_user_ids", id))
      const result = await getBatchApi({
        urlBuilderParams: { batchId },
        searchParams,
      })
      if (!result?.data) throw new Error("Missing saved selection")
      return result.data.items.map((item) => item.client_user_id)
    },
    [getBatchApi]
  )

  const acceptCreated = useCallback(
    (created: ReplyModeBatch, pending: PendingCreate) => {
      pendingCreateRef.current = null
      update({
        batch: created,
        source: pending.input.source,
        interrupted: false,
        phase: "ready",
      })
      return created
    },
    [update]
  )

  /** POST with the same caller-generated ID; recover by GET when unknown. */
  const submitCreate = useCallback(
    async (pending: PendingCreate, generation: number) => {
      const isStale = () => generationRef.current !== generation
      try {
        const result = await createBatchApi({
          jsonBody: buildCreateBody(pending.id, pending.input),
          retry: 0,
        })
        if (isStale()) return null
        if (result?.data) return acceptCreated(result.data, pending)
      } catch (error) {
        if (isStale()) return null
        if (isDefinitiveRejection(error)) {
          pendingCreateRef.current = null
          update({ phase: "idle" })
          onRequestErrorRef.current?.(error)
          return null
        }
      }

      // Unknown outcome: the snapshot may exist. Read it back by ID first.
      try {
        const saved = await readSavedBatch(pending.id)
        if (isStale()) return null
        if (saved) return acceptCreated(saved, pending)
      } catch {
        if (isStale()) return null
      }
      update({ phase: "createUnknown" })
      return null
    },
    [acceptCreated, createBatchApi, readSavedBatch, update]
  )

  const createBatch = useCallback(
    async (input: CreateReplyModeBatchInput) => {
      if (inFlightRef.current || pendingCreateRef.current) return null
      inFlightRef.current = true
      const generation = ++generationRef.current
      const pending: PendingCreate = { id: uuid(), input }
      pendingCreateRef.current = pending
      update({ ...INITIAL_STATE, source: input.source, phase: "creating" })
      try {
        return await submitCreate(pending, generation)
      } finally {
        if (generationRef.current === generation) inFlightRef.current = false
      }
    },
    [submitCreate, update]
  )

  /** The server processes 50 pending items per request; repeat until done. */
  const runChunks = useCallback(
    async (mode: ReplyModeTarget, retry: boolean) => {
      const start = stateRef.current.batch
      if (!start || inFlightRef.current) return
      inFlightRef.current = true
      const generation = generationRef.current
      const isStale = () => generationRef.current !== generation
      update({ interrupted: false, requestedMode: mode, phase: "executing" })

      try {
        for (let first = true; ; first = false) {
          const result = await executeBatchApi({
            urlBuilderParams: { batchId: start.id },
            jsonBody: {
              interaction_mode: mode,
              ...(retry && first ? { retry: true } : {}),
            },
            retry: 0,
          })
          if (isStale()) return
          if (!result?.data) throw new Error("Empty reply mode batch response")
          update({ batch: result.data })
          if (result.data.pending === 0) break
        }
        update({ phase: "ready" })
      } catch (error) {
        if (isStale()) return
        if (isDefinitiveRejection(error)) onRequestErrorRef.current?.(error)
        // Never assume failure: reconcile against saved server state first.
        await reconcile(start.id, isStale, "unknown")
      } finally {
        if (!isStale()) inFlightRef.current = false
      }
    },
    [executeBatchApi, reconcile, update]
  )

  const loadFailedPage = useCallback(
    async (after?: string) => {
      const current = stateRef.current.batch
      if (!current || inFlightRef.current) return
      inFlightRef.current = true
      const generation = generationRef.current
      update({ phase: "reconciling" })
      try {
        const saved = await readSavedBatch(current.id, after)
        if (generationRef.current === generation && saved)
          update({ batch: saved })
      } catch (error) {
        if (generationRef.current === generation)
          onRequestErrorRef.current?.(error)
      } finally {
        if (generationRef.current === generation) {
          inFlightRef.current = false
          update({ phase: "ready" })
        }
      }
    },
    [readSavedBatch, update]
  )

  const execute = useCallback(
    (mode: ReplyModeTarget) => {
      const frozen = stateRef.current.batch?.interaction_mode
      if (frozen && frozen !== mode) return Promise.resolve()
      return runChunks(mode, false)
    },
    [runChunks]
  )

  /** Resume the current command, optionally re-queueing failed items first. */
  const resume = useCallback(
    (retry: boolean) => {
      const { batch, requestedMode } = stateRef.current
      const mode = batch?.interaction_mode ?? requestedMode
      if (!mode) return Promise.resolve()
      return runChunks(mode, retry)
    },
    [runChunks]
  )
  const continuePending = useCallback(() => resume(false), [resume])
  const retryFailed = useCallback(() => resume(true), [resume])

  /**
   * Re-read saved state. For an unknown create, GET by the caller-generated
   * ID and only re-POST (same ID, idempotent) when it is confirmed missing.
   */
  const recheck = useCallback(async (): Promise<ReplyModeBatch | null> => {
    if (inFlightRef.current) return null
    const currentPhase = stateRef.current.phase
    const generation = generationRef.current
    const isStale = () => generationRef.current !== generation

    if (currentPhase === "createUnknown") {
      const pending = pendingCreateRef.current
      if (!pending) return null
      inFlightRef.current = true
      update({ phase: "creating" })
      try {
        let saved: ReplyModeBatch | null = null
        try {
          saved = await readSavedBatch(pending.id)
        } catch (error) {
          if (isStale()) return null
          if (getStatus(error) !== 404) {
            update({ phase: "createUnknown" })
            return null
          }
        }
        if (isStale()) return null
        return saved
          ? acceptCreated(saved, pending)
          : await submitCreate(pending, generation)
      } finally {
        if (!isStale()) inFlightRef.current = false
      }
    }

    const current = stateRef.current.batch
    if (!current) return null
    inFlightRef.current = true
    try {
      return await reconcile(
        current.id,
        isStale,
        currentPhase === "unknown" ? "unknown" : "ready"
      )
    } finally {
      if (!isStale()) inFlightRef.current = false
    }
  }, [acceptCreated, readSavedBatch, reconcile, submitCreate, update])

  const { phase } = state
  const busy =
    phase === "creating" || phase === "executing" || phase === "reconciling"

  return {
    ...state,
    busy,
    canCreate: !busy && phase !== "createUnknown",
    createBatch,
    execute,
    continuePending,
    retryFailed,
    loadFailedPage,
    getSelectedPageIds,
    recheck,
    reset,
    resetIfNotStarted,
  }
}

export type ReplyModeBatchController = ReturnType<typeof useReplyModeBatch>
